import { ReactNode, useEffect, useRef, useState } from 'react';
import { usePageLoadStatus } from 'src/hooks/usePageLoadStatus';

import { PageLoadErrorBoundary } from './PageLoadErrorBoundary';

interface LoadingTrackerProps {
  componentName: string;
  children: ReactNode;
  /** Optional: Custom loading condition. If not provided, waits for component mount + next tick */
  isLoaded?: boolean;
  /** Optional: How long to wait before considering component loaded (in ms). Default: 100ms */
  loadingDelay?: number;
  /** Optional: Whether to wrap with error boundary. Default: true */
  useErrorBoundary?: boolean;
}

export function LoadingTracker({
  componentName,
  children,
  isLoaded,
  loadingDelay = 200,
  useErrorBoundary = true,
}: LoadingTrackerProps) {
  const { markComponentLoaded, markComponentError } = usePageLoadStatus();
  const [hasBeenMarkedLoaded, setHasBeenMarkedLoaded] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // If isLoaded prop is provided, use that as the condition
    if (isLoaded !== undefined) {
      if (isLoaded && !hasBeenMarkedLoaded) {
        markComponentLoaded(componentName);
        setHasBeenMarkedLoaded(true);
      }
      return;
    }

    // Otherwise, wait for a short delay to ensure component has fully rendered
    timeoutRef.current = setTimeout(() => {
      try {
        if (!hasBeenMarkedLoaded) {
          markComponentLoaded(componentName);
          setHasBeenMarkedLoaded(true);
        }
      } catch (error) {
        markComponentError(
          componentName,
          error instanceof Error ? error : new Error(String(error))
        );
      }
    }, loadingDelay);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [
    componentName,
    isLoaded,
    hasBeenMarkedLoaded,
    loadingDelay,
    markComponentLoaded,
    markComponentError,
  ]);

  // Error boundary-like behavior for this component
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      if (event.error) {
        markComponentError(componentName, event.error);
      }
    };

    window.addEventListener('error', handleError);

    return () => {
      window.removeEventListener('error', handleError);
    };
  }, [componentName, markComponentError]);

  const content = <>{children}</>;

  if (useErrorBoundary) {
    return (
      <PageLoadErrorBoundary componentName={componentName}>
        {content}
      </PageLoadErrorBoundary>
    );
  }

  return content;
}
